/**
 * PAY — hours, what was earned, what was handed over, what is still owed.
 *
 * The old system had this as FOUR separate screens: Hours, Payroll,
 * Payouts and My Pay. They are four views of one subtraction:
 *
 *     earned (hours x rate, plus commission, plus reimbursements)
 *   - paid   (what has actually been handed over)
 *   = owed
 *
 * Splitting that across four menu items is how somebody ends up looking at
 * hours on one screen and payments on another and doing the sum in their
 * head. It is one screen with tabs here, and the tabs are the same four
 * words so nobody has to relearn where anything is.
 *
 * MONEY IS NEVER INFERRED FROM A STATUS. A payout row is the only proof
 * somebody was paid — not a job being closed, not a week having passed.
 * That mattered enough to the last system's problems to be worth saying
 * twice.
 */

import { db } from "./client.ts";
import { valueOf, shareOf, termsFor as pairFor, cents } from "../domain/commission.ts";
import { splitOvertime, splitPay, addSplits, type Split } from "../domain/overtime.ts";
import type { Punch } from "../domain/payroll-export.ts";

const DAY = 86400000;

export type Hours = {
  id: string; technician_id: string; worked_on: string;
  hours: number; rate: number | null; note: string | null; job_id: string | null;
};

export type Payout = {
  id: string; technician_id: string; amount: number; method: string;
  covers_from: string | null; covers_to: string | null; reference: string | null; paid_at: string;
};

export type Terms = {
  technician_id: string; basis: string | null;
  hourly_rate: number | null; commission_pct: number | null; parts_pct: number | null;
  shop_commission_pct: number | null; shop_parts_pct: number | null;
  role: string | null; settles_own: boolean | null;
};

export type Tech = { id: string; name: string; active: boolean; profile_id: string | null };

/** One person's money, over one window. */
export type Line = {
  tech: Tech;
  hours: number;
  /** Of those hours, how many were time and a half and double (domain/overtime.ts). */
  split: Split;
  fromHours: number;
  commission: number;
  /**
   * THE TWO HALVES OF THE COMMISSION, so the screen can say the rule
   * rather than one opaque number.
   *
   * Anthony: "mobile work paid at two thirds of labor plus half the parts
   * markup." A technician who can see 67% of $225 plus 50% of $275 can
   * check their own pay against the job in front of them. A single
   * "$294.23 commission" is a number you either believe or argue about.
   *
   * These are the GROSS figures the percentages were applied to, not the
   * shares -- see src/domain/commission.ts, which had to pick one
   * convention because the hand-entered rows used both.
   */
  commLabour: number;
  commParts: number;
  reimbursed: number;
  earned: number;
  paid: number;
  owed: number;
  /**
   * WHAT IS OWED ACROSS ALL TIME, beside what is owed this period.
   *
   * A window can show a negative and be perfectly correct: somebody paid
   * this week for work done last month earned nothing new and took money,
   * which reads as an overpayment and is not one. The all-time figure is
   * the one that says whether the shop actually owes them, and it is the
   * number that stops a person being paid twice for the same fortnight.
   */
  owedAllTime: number;
};

/** Monday of the week a date falls in, so weeks line up with payroll. */
export function weekStart(d = new Date()): string {
  const x = new Date(d);
  const back = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - back);
  x.setHours(0, 0, 0, 0);
  return x.toISOString().slice(0, 10);
}

export const addDays = (iso: string, n: number): string =>
  new Date(new Date(iso).getTime() + n * DAY).toISOString().slice(0, 10);

const num = (v: unknown) => Number(v ?? 0) || 0;

export async function technicians(): Promise<Tech[]> {
  const sb = await db();
  const { data, error } = await sb.from("technicians")
    .select("id,name,active,profile_id").order("name");
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Tech[];
}

export async function termsFor(): Promise<Map<string, Terms>> {
  const sb = await db();
  const { data } = await sb.from("pay_terms")
    .select("technician_id,basis,hourly_rate,commission_pct,parts_pct,shop_commission_pct,shop_parts_pct,role,settles_own");
  return new Map(((data ?? []) as unknown as Terms[]).map((t) => [t.technician_id, t]));
}

export async function hoursIn(from: string, to: string, techId?: string): Promise<Hours[]> {
  const sb = await db();
  let q = sb.from("pay_hours").select("id,technician_id,worked_on,hours,rate,note,job_id")
    .gte("worked_on", from).lte("worked_on", to)
    .order("worked_on", { ascending: false }).limit(2000);
  if (techId) q = q.eq("technician_id", techId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Hours[];
}

/**
 * A PAYMENT BELONGS TO THE WORK IT SETTLES, NOT THE DAY IT LEFT.
 *
 * Anthony: "payroll is a little glitched... if it looks like they got
 * overpaid or underpaid cause the old data is flawed, fix this. I use this
 * to pay people."
 *
 * Four people read as overpaid in the last fortnight, Robert wilson by
 * £1,342. Nobody was: checked across all time, every balance is zero or
 * money still owed. Four catch-up payments made on 31 August and 3
 * September settled work earned weeks earlier, and this filed them by the
 * day they were handed over, so the earnings sat outside the window and
 * the payment sat inside it.
 *
 * `covers_to` has been on this table since it was created for exactly
 * this. Nothing was reading it. A payment that SAYS which period it
 * settles is now filed there; one that says nothing still falls where it
 * landed, because guessing is how a payroll record stops being evidence.
 *
 * A row is fetched if EITHER date lands in the window, and then filtered
 * on the effective one — a `or` across two columns in PostgREST is a
 * fragile thing to write and a cheap thing to do in code.
 */
export const coversOn = (p: { covers_to: string | null; paid_at: string }): string =>
  (p.covers_to ?? p.paid_at).slice(0, 10);

export async function payoutsIn(from: string, to: string, techId?: string): Promise<Payout[]> {
  const sb = await db();
  // Reach back far enough to catch anything DATED into this window from an
  // earlier payment, and forward for the reverse.
  const wide = (d: string, days: number) =>
    new Date(Date.parse(`${d}T00:00:00Z`) + days * 864e5).toISOString().slice(0, 10);
  let q = sb.from("pay_payments")
    .select("id,technician_id,amount,method,covers_from,covers_to,reference,paid_at")
    .gte("paid_at", `${wide(from, -400)}T00:00:00Z`)
    .lte("paid_at", `${wide(to, 400)}T23:59:59Z`)
    .order("paid_at", { ascending: false }).limit(4000);
  if (techId) q = q.eq("technician_id", techId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as Payout[])
    .filter((x) => coversOn(x) >= from && coversOn(x) <= to);
}

/**
 * The whole subtraction, per person, for one window.
 *
 * A rate on the hours row WINS over the rate in terms, because the row
 * records what was actually agreed that day. Changing somebody's rate
 * today must not silently rewrite what they earned last March.
 */
export async function payrun(from: string, to: string): Promise<Line[]> {
  const sb = await db();
  const [techs, terms, hrs, pays, comms, reimb] = await Promise.all([
    technicians(),
    termsFor(),
    hoursIn(from, to),
    payoutsIn(from, to),
    sb.from("pay_commissions").select("technician_id,amount,labour_amount,parts_markup")
      .gte("earned_at", `${from}T00:00:00Z`).lte("earned_at", `${to}T23:59:59Z`).limit(2000),
    sb.from("pay_reimbursements").select("technician_id,amount")
      .gte("spent_on", from).lte("spent_on", to).limit(1000),
  ]);

  const sum = <T,>(rows: T[], key: (r: T) => string, val: (r: T) => number) => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + val(r));
    return m;
  };

  // The whole ledger, so a window that looks like an overpayment can be
  // read against the only figure that settles the question.
  const lifetime = await owedEver();

  const hoursBy = sum(hrs, (h) => h.technician_id, (h) => num(h.hours));
  const splits = splitOvertime(hrs);
  const splitOf = (h: Hours): Split => splits.get(h.id) ?? { reg: num(h.hours), ot: 0, dt: 0 };
  const earnedByHours = sum(hrs, (h) => h.technician_id,
    (h) => splitPay(splitOf(h), h.rate != null ? num(h.rate) : num(terms.get(h.technician_id)?.hourly_rate)));
  const paidBy = sum(pays, (p) => p.technician_id, (p) => num(p.amount));
  type Comm = {
    technician_id: string; amount: number;
    labour_amount: number | null; parts_markup: number | null;
  };
  const commRows = (comms.data ?? []) as Comm[];
  const commBy = sum(commRows, (c) => c.technician_id, (c) => num(c.amount));
  const commLabourBy = sum(commRows, (c) => c.technician_id, (c) => num(c.labour_amount));
  const commPartsBy = sum(commRows, (c) => c.technician_id, (c) => num(c.parts_markup));
  const reimbBy = sum((reimb.data ?? []) as Array<{ technician_id: string; amount: number }>,
    (r) => r.technician_id, (r) => num(r.amount));

  return techs
    .map((tech) => {
      const fromHours = earnedByHours.get(tech.id) ?? 0;
      const commission = commBy.get(tech.id) ?? 0;
      const reimbursed = reimbBy.get(tech.id) ?? 0;
      const earned = fromHours + commission + reimbursed;
      const paid = paidBy.get(tech.id) ?? 0;
      return {
        tech, hours: hoursBy.get(tech.id) ?? 0,
        split: addSplits(hrs.filter((h) => h.technician_id === tech.id).map(splitOf)),
        fromHours, commission,
        commLabour: commLabourBy.get(tech.id) ?? 0,
        commParts: commPartsBy.get(tech.id) ?? 0,
        reimbursed, earned, paid, owed: earned - paid,
        owedAllTime: lifetime.get(tech.id) ?? 0,
      };
    })
    // Somebody with nothing in the window is not part of this pay run.
    .filter((l) => l.hours || l.earned || l.paid)
    .sort((a, b) => b.owed - a.owed);
}

/**
 * EVERYTHING EVER EARNED MINUS EVERYTHING EVER PAID, per person.
 *
 * No window at all, on purpose. This is the number that answers "do we owe
 * this person money", and it is the only one immune to a payment and its
 * work falling on opposite sides of a date.
 */
export async function owedEver(): Promise<Map<string, number>> {
  const sb = await db();
  const [terms, hrs, pays, comms, reimb] = await Promise.all([
    termsFor(),
    sb.from("pay_hours").select("id,technician_id,worked_on,hours,rate").limit(5000),
    sb.from("pay_payments").select("technician_id,amount").limit(5000),
    sb.from("pay_commissions").select("technician_id,amount").limit(5000),
    sb.from("pay_reimbursements").select("technician_id,amount").limit(2000),
  ]);

  const out = new Map<string, number>();
  const add = (id: string, n: number) => out.set(id, (out.get(id) ?? 0) + n);

  const hourRows = (hrs.data ?? []) as Array<{
    id: string; technician_id: string; worked_on: string; hours: number; rate: number | null }>;
  const splits = splitOvertime(hourRows);
  for (const h of hourRows) {
    add(h.technician_id, splitPay(splits.get(h.id) ?? { reg: num(h.hours), ot: 0, dt: 0 },
      h.rate != null ? num(h.rate) : num(terms.get(h.technician_id)?.hourly_rate)));
  }
  for (const c of (comms.data ?? []) as Array<{ technician_id: string; amount: number }>) {
    add(c.technician_id, num(c.amount));
  }
  for (const r of (reimb.data ?? []) as Array<{ technician_id: string; amount: number }>) {
    add(r.technician_id, num(r.amount));
  }
  for (const p of (pays.data ?? []) as Array<{ technician_id: string; amount: number }>) {
    add(p.technician_id, -num(p.amount));
  }
  return out;
}

/**
 * SAY WHICH PERIOD A PAYMENT SETTLES.
 *
 * The reason this exists: David's two Zelle payments on 31 August carry no
 * period and nothing on them says which week they were for, so the pay run
 * files them where they landed and he reads as $234 overpaid when he is in
 * fact owed $206. I would not guess at a date to tidy that number -- a
 * payroll record that has been guessed at stops being evidence.
 *
 * So the person who knows can say. It is the only honest way to close the
 * gap, and having it means the next catch-up payment is filed correctly on
 * the day it is made rather than found weeks later.
 */
export async function setCovers(
  paymentId: string, from: string | null, to: string | null,
): Promise<void> {
  if (from && to && from > to) throw new Error("That period runs backwards.");
  const sb = await db();
  const { error } = await sb.from("pay_payments")
    .update({ covers_from: from || null, covers_to: to || null })
    .eq("id", paymentId);
  if (error) throw new Error(error.message);
}

export async function logHours(
  technicianId: string, workedOn: string, hours: number, rate: number | null, note: string,
): Promise<void> {
  if (!(hours > 0)) throw new Error("Zero hours is not a shift.");
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Cannot tell which shop you belong to.");

  const { error } = await sb.from("pay_hours").insert({
    account_id: accountId, technician_id: technicianId, worked_on: workedOn,
    hours, rate, note: note.trim() || null,
  });
  if (error) throw new Error(error.message);
}

export async function recordPayout(
  technicianId: string, amount: number, method: string,
  from: string, to: string, reference: string,
): Promise<void> {
  if (!(amount > 0)) throw new Error("A payout has to be an amount.");
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Cannot tell which shop you belong to.");

  const { error } = await sb.from("pay_payments").insert({
    account_id: accountId, technician_id: technicianId, amount, method,
    covers_from: from, covers_to: to, reference: reference.trim() || null,
    recorded_by: me.user?.id ?? null,
  });
  if (error) throw new Error(error.message);
}

/**
 * CLOCK IN AND OUT, for hourly people, from My pay.
 *
 * All three go through database functions (0191) that take the person from
 * the login, never from an argument, so nobody can punch for a colleague.
 * Clock-out writes an ordinary pay_hours row dated by the Pacific day the
 * shift started, so payroll reads it like any shift an admin typed in.
 *
 * BEFORE 0191 IS APPLIED the functions do not exist. clockStatus() then
 * says "not hourly" and the card stays hidden, rather than putting a
 * database error in front of a technician.
 */
export type ClockStatus = {
  linked: boolean; hourly: boolean; open_since: string | null;
  /** 0192. Absent before it is applied, which reads as "not on a break". */
  on_break_since?: string | null; break_minutes?: number;
};

export async function clockStatus(): Promise<ClockStatus> {
  const sb = await db();
  const { data, error } = await sb.rpc("clock_status");
  if (error || !data) return { linked: false, hourly: false, open_since: null };
  return data as unknown as ClockStatus;
}

export async function clockIn(): Promise<void> {
  const sb = await db();
  const { error } = await sb.rpc("clock_in");
  if (error) throw new Error(error.message);
}

export type ClockOut = {
  cancelled: boolean; hours: number; worked_on?: string; note?: string; flags?: string[];
};

/** A meal break, inside an open shift. 30 minutes or more is unpaid. */
export async function breakStart(): Promise<void> {
  const sb = await db();
  const { error } = await sb.rpc("break_start");
  if (error) throw new Error(error.message);
}

export async function breakEnd(): Promise<void> {
  const sb = await db();
  const { error } = await sb.rpc("break_end");
  if (error) throw new Error(error.message);
}

export async function clockOut(): Promise<ClockOut> {
  const sb = await db();
  const { data, error } = await sb.rpc("clock_out");
  if (error) throw new Error(error.message);
  return data as unknown as ClockOut;
}

/** Who in the shop is on the clock right now, for the Hours tab. */
export async function onTheClock(): Promise<Array<{ technician_id: string; clock_in: string }>> {
  const sb = await db();
  const { data, error } = await sb.from("pay_clock")
    .select("technician_id,clock_in").is("clock_out", null).order("clock_in");
  if (error) return [];
  return (data ?? []) as unknown as Array<{ technician_id: string; clock_in: string }>;
}

/**
 * The clock punches whose shift could fall in a window, with their meal
 * breaks, for the payroll export. Read through the same policy as every
 * other pay read: a technician gets their own, the desk gets the shop's.
 * The window is padded a day each side because the row is dated by the
 * Pacific day it started and the filter is UTC; punchesForWeek() in
 * src/domain/payroll-export.ts trims it to the week shown.
 */
export async function punchesIn(from: string, to: string): Promise<Punch[]> {
  const sb = await db();
  const { data, error } = await sb.from("pay_clock")
    .select("id,technician_id,clock_in,clock_out,pay_clock_breaks(started_at,ended_at)")
    .gte("clock_in", `${addDays(from, -1)}T00:00:00Z`)
    .lt("clock_in", `${addDays(to, 2)}T00:00:00Z`)
    .order("clock_in").limit(2000);
  if (error) throw new Error(error.message);
  type Row = Omit<Punch, "breaks"> & { pay_clock_breaks: Punch["breaks"] | null };
  return ((data ?? []) as unknown as Row[]).map(({ pay_clock_breaks, ...p }) =>
    ({ ...p, breaks: pay_clock_breaks ?? [] }));
}

/** Which technician record is the signed-in person, if any. */
export async function meAsTech(): Promise<Tech | null> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  if (!me.user) return null;
  const { data } = await sb.from("technicians")
    .select("id,name,active,profile_id").eq("profile_id", me.user.id).maybeSingle();
  return (data as unknown as Tech) ?? null;
}

/**
 * CORRECTING AN HOURS ROW.
 *
 * Payroll is the one place where a wrong number is somebody's wages, and
 * the only thing worse than a mistake is a mistake that leaves no trace.
 * So a correction is not a silent overwrite: the old figures and the
 * reason are appended to the note, and what the row used to say stays
 * readable underneath what it says now.
 *
 * A REASON IS REQUIRED. "Corrected 8h to 6h" tells the next person
 * nothing; "clocked out at the shop, not at the customer" tells them
 * everything, and it is the sentence somebody will want in three months
 * when a technician disputes a week.
 */
export async function correctHours(
  id: string,
  next: { hours: number; rate: number | null; worked_on: string },
  why: string,
): Promise<void> {
  if (!(next.hours > 0)) throw new Error("Zero hours is not a shift.");
  if (!why.trim()) throw new Error("Say why it changed — that is the whole point of correcting it here.");

  const sb = await db();
  const { data: was } = await sb.from("pay_hours")
    .select("hours,rate,worked_on,note").eq("id", id).maybeSingle();
  const before = was as { hours: number; rate: number | null; worked_on: string; note: string | null } | null;

  const trail = before
    ? `[${new Date().toISOString().slice(0, 10)}] was ${Number(before.hours).toFixed(2)}h` +
      `${before.rate != null ? ` at ${Number(before.rate).toFixed(2)}` : ""}` +
      ` on ${before.worked_on} — ${why.trim()}`
    : why.trim();

  const { error } = await sb.from("pay_hours").update({
    hours: next.hours,
    rate: next.rate,
    worked_on: next.worked_on,
    note: [before?.note, trail].filter(Boolean).join("\n"),
  }).eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Removing an hours row entirely — a shift logged against the wrong
 * person, or twice. The reason goes on the job's own record rather than
 * vanishing with the row, because a week that shrinks with no explanation
 * is how a technician stops trusting the numbers.
 */
export async function removeHours(id: string, why: string): Promise<void> {
  if (!why.trim()) throw new Error("Say why it is going.");

  const sb = await db();
  const { data: row } = await sb.from("pay_hours")
    .select("technician_id,worked_on,hours,rate,job_id,account_id").eq("id", id).maybeSingle();
  const r = row as {
    technician_id: string; worked_on: string; hours: number;
    rate: number | null; job_id: string | null; account_id: string;
  } | null;

  if (r?.job_id) {
    const { data: me } = await sb.auth.getUser();
    await sb.from("job_events").insert({
      account_id: r.account_id, job_id: r.job_id,
      kind: "hours_removed", actor: "staff", actor_id: me.user?.id ?? null,
      title: `${Number(r.hours).toFixed(2)} hours removed from ${r.worked_on}`,
      detail: why.trim(),
      visible_to_customer: false,
    });
  }

  const { error } = await sb.from("pay_hours").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/* ── the statement: one person, one window, every line that makes the number ── */

/**
 * KUBERA'S STATEMENT.
 *
 * Anthony worked David's pay out by hand on 2026-10-03: two labor amounts,
 * two thirds of them, half the markup on an alternator once the core
 * refund came off its cost, a $25 review, minus the $150 cash David had
 * kept. Every one of those is a row in this database or should be, and the
 * statement is those rows laid out in the order he did the sum, so the
 * next one is read rather than worked out.
 */
export type StatementJob = {
  commissionId: string;
  jobId: string | null;
  jobNumber: string | null;
  title: string | null;
  serviceAt: string | null;
  basis: string;
  earnedAt: string;
  labour: number;
  partsMarkup: number;
  amount: number;
  note: string | null;
  labourPct: number;
  partsPct: number;
  parts: Array<{
    id: string; description: string; quantity: number;
    sold: number; cost: number | null; isCore: boolean;
  }>;
};

export type Statement = {
  tech: Tech;
  terms: Terms | null;
  jobs: StatementJob[];
  bonuses: StatementJob[];
  hours: Hours[];
  split: Split;
  fromHours: number;
  reimbursements: Array<{ id: string; amount: number; reason: string; spent_on: string | null }>;
  /** Money the person took from a customer and kept. Paid to them, in effect. */
  keptCash: Payout[];
  /** Money the shop handed over. */
  payouts: Payout[];
  earned: number;
  paid: number;
  owed: number;
  owedAllTime: number;
};

/** The reference a kept-cash row carries, so the statement can tell it from a payout. */
export const KEPT_CASH = "cash collected from customer";

export const isKeptCash = (p: Payout): boolean =>
  p.method === "cash" && /collected|kept/i.test(p.reference ?? "");

export async function statement(techId: string, from: string, to: string): Promise<Statement> {
  const sb = await db();
  const [techs, terms, hrs, pays, comms, reimb, lifetime] = await Promise.all([
    technicians(), termsFor(), hoursIn(from, to, techId), payoutsIn(from, to, techId),
    sb.from("pay_commissions")
      .select("id,job_id,basis,labour_amount,parts_markup,amount,note,earned_at,"
        + "jobs(job_number,title,service_at,line_items(id,kind,description,quantity,total,cost,is_core))")
      .eq("technician_id", techId)
      .gte("earned_at", `${from}T00:00:00Z`).lte("earned_at", `${to}T23:59:59Z`)
      .order("earned_at").limit(500),
    sb.from("pay_reimbursements").select("id,amount,reason,spent_on")
      .eq("technician_id", techId).gte("spent_on", from).lte("spent_on", to).limit(200),
    owedEver(),
  ]);
  if (comms.error) throw new Error(comms.error.message);

  const tech = techs.find((t) => t.id === techId);
  if (!tech) throw new Error("No such person on this shop.");
  const t = terms.get(techId) ?? null;

  type Row = {
    id: string; job_id: string | null; basis: string; labour_amount: number; parts_markup: number;
    amount: number; note: string | null; earned_at: string;
    jobs: { job_number: string; title: string | null; service_at: string | null;
      line_items: Array<{ id: string; kind: string; description: string; quantity: number;
        total: number; cost: number | null; is_core: boolean }> | null } | null;
  };
  const lines: StatementJob[] = ((comms.data ?? []) as unknown as Row[]).map((c) => {
    const pair = t ? pairFor(t, c.jobs?.service_at) : { commission_pct: 0, parts_pct: 0 };
    return {
      commissionId: c.id, jobId: c.job_id, jobNumber: c.jobs?.job_number ?? null,
      title: c.jobs?.title ?? null, serviceAt: c.jobs?.service_at ?? null,
      basis: c.basis, earnedAt: c.earned_at,
      labour: num(c.labour_amount), partsMarkup: num(c.parts_markup), amount: num(c.amount),
      note: c.note,
      labourPct: num(pair.commission_pct), partsPct: num(pair.parts_pct),
      parts: (c.jobs?.line_items ?? []).filter((l) => l.kind === "part").map((l) => ({
        id: l.id, description: l.description, quantity: num(l.quantity) || 1,
        sold: num(l.total), cost: l.cost == null ? null : num(l.cost), isCore: !!l.is_core,
      })),
    };
  });

  // Overtime is worked out over the whole week, so the statement asks for
  // the same Monday-to-Sunday window the pay run uses.
  const splits = splitOvertime(hrs);
  const splitOf = (h: Hours): Split => splits.get(h.id) ?? { reg: num(h.hours), ot: 0, dt: 0 };
  const fromHours = hrs.reduce((n, h) =>
    n + splitPay(splitOf(h), h.rate != null ? num(h.rate) : num(t?.hourly_rate)), 0);
  const reimbursements = (reimb.data ?? []) as Statement["reimbursements"];
  const keptCash = pays.filter(isKeptCash);
  const payouts = pays.filter((p) => !isKeptCash(p));
  const earned = fromHours + lines.reduce((n, l) => n + l.amount, 0)
    + reimbursements.reduce((n, r) => n + num(r.amount), 0);
  const paid = pays.reduce((n, p) => n + num(p.amount), 0);

  return {
    tech, terms: t,
    jobs: lines.filter((l) => l.basis !== "bonus" && l.basis !== "dispatch"),
    bonuses: lines.filter((l) => l.basis === "bonus" || l.basis === "dispatch"),
    hours: hrs, split: addSplits(hrs.map(splitOf)), fromHours, reimbursements, keptCash, payouts,
    earned: cents(earned), paid: cents(paid), owed: cents(earned - paid),
    owedAllTime: cents(lifetime.get(techId) ?? 0),
  };
}

async function myShop(): Promise<{ accountId: string; userId: string | null }> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Cannot tell which shop you belong to.");
  return { accountId, userId: me.user?.id ?? null };
}

/**
 * A BONUS: a review, a referral, a dispatch call. Earned without a repair
 * behind it, so it has no job and says what it was for instead.
 */
export async function addBonus(
  technicianId: string, amount: number, why: string, on: string,
  basis: "bonus" | "dispatch" = "bonus",
): Promise<void> {
  if (!(amount > 0)) throw new Error("A bonus has to be an amount.");
  if (!why.trim()) throw new Error("Say what it was for, so the statement can.");
  const sb = await db();
  const { accountId } = await myShop();
  const { error } = await sb.from("pay_commissions").insert({
    account_id: accountId, technician_id: technicianId, job_id: null, basis,
    labour_amount: 0, parts_markup: 0, amount: cents(amount), note: why.trim(),
    earned_at: `${on}T12:00:00Z`,
  });
  if (error) throw new Error(error.message);
}

export async function removeBonus(commissionId: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("pay_commissions").delete()
    .eq("id", commissionId).in("basis", ["bonus", "dispatch"]);
  if (error) throw new Error(error.message);
}

/**
 * CASH THE TECH KEPT. A customer paid David $150 in cash and he kept it.
 * That is $150 of his pay already in his pocket, so it is recorded as a
 * cash payment, which is what it is, with a reference that lets the
 * statement show it as collected rather than as something the shop handed
 * over.
 */
export async function recordKeptCash(
  technicianId: string, amount: number, on: string, jobNote: string,
): Promise<void> {
  if (!(amount > 0)) throw new Error("Cash kept has to be an amount.");
  const sb = await db();
  const { accountId, userId } = await myShop();
  const { error } = await sb.from("pay_payments").insert({
    account_id: accountId, technician_id: technicianId, amount: cents(amount), method: "cash",
    covers_from: on, covers_to: on,
    reference: [KEPT_CASH, jobNote.trim()].filter(Boolean).join(" · "),
    paid_at: `${on}T12:00:00Z`, recorded_by: userId,
  });
  if (error) throw new Error(error.message);
}

/**
 * WHAT A PART REALLY COST, and the commission worked out again from it.
 *
 * The job carries the cost the quote guessed ($220 for David's
 * alternator). The shop paid $261.71 and got $43.50 back for the core, so
 * the real cost is $218.21. A core refund that came back to the shop comes
 * off the cost. One the tech kept is cash in their pocket and goes on as
 * kept cash instead.
 *
 * A new cost is a new markup, so every commission row on the job is
 * recomputed with the same rule the nightly accrual uses. This is a person
 * at the screen deciding, which is the one place an existing pay row is
 * allowed to change.
 */
export async function setPartCost(
  lineId: string, paid: number, coreBack: number, jobId: string,
): Promise<void> {
  if (!(paid >= 0)) throw new Error("What did the part cost?");
  if (coreBack < 0 || coreBack > paid) throw new Error("The core refund cannot be more than the part.");
  const sb = await db();
  const { data: line } = await sb.from("line_items").select("quantity").eq("id", lineId).maybeSingle();
  const qty = num((line as { quantity: number } | null)?.quantity) || 1;
  // cost on line_items is per unit.
  const { data: moved, error } = await sb.from("line_items")
    .update({ cost: cents((paid - coreBack) / qty) }).eq("id", lineId).select("id");
  if (error) throw new Error(error.message);
  if (!moved?.length) throw new Error("That part line could not be changed from this login.");
  await recomputeJob(jobId);
}

/** Every commission row on one job, worked out again from its lines. */
export async function recomputeJob(jobId: string): Promise<void> {
  const sb = await db();
  const [{ data: job, error }, terms] = await Promise.all([
    sb.from("jobs").select("service_at,line_items(kind,total,cost,quantity),"
      + "job_technicians(technician_id),pay_commissions(id,technician_id,basis)")
      .eq("id", jobId).maybeSingle(),
    termsFor(),
  ]);
  if (error || !job) throw new Error(error?.message ?? "That job is gone.");
  const j = job as unknown as {
    service_at: string | null;
    line_items: Array<{ kind: string; total: number; cost: number | null; quantity: number }>;
    job_technicians: Array<{ technician_id: string }>;
    pay_commissions: Array<{ id: string; technician_id: string; basis: string }>;
  };
  const value = valueOf(j.line_items ?? []);
  const hands = (j.job_technicians ?? []).length || 1;
  for (const c of j.pay_commissions ?? []) {
    if (c.basis !== "commission") continue;
    const t = terms.get(c.technician_id);
    if (!t) continue;
    const s = shareOf(value, pairFor(t, j.service_at), hands);
    const { data: moved, error: e } = await sb.from("pay_commissions").update({
      labour_amount: s.labourAmount, parts_markup: s.partsMarkup, amount: s.amount,
    }).eq("id", c.id).select("id");
    if (e) throw new Error(e.message);
    if (!moved?.length) throw new Error("A commission row on that job could not be updated from this login.");
  }
}

/** The pay plan screen writes here, and only the fields it shows. */
export async function savePlan(technicianId: string, plan: {
  role: string | null; basis: string | null; hourly_rate: number | null;
  commission_pct: number | null; parts_pct: number | null;
  shop_commission_pct: number | null; shop_parts_pct: number | null;
}): Promise<void> {
  const sb = await db();
  const { accountId } = await myShop();
  const { error } = await sb.from("pay_terms").upsert({
    technician_id: technicianId, account_id: accountId, ...plan,
    updated_at: new Date().toISOString(),
  }, { onConflict: "technician_id" });
  if (error) throw new Error(error.message);
}
